// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  login,
  logout,
  register,
  getCurrentUser,
  verifyEmail,
  resendVerification,
  requestPasswordReset,
  adicionarEmailRecuperacao,
  resetPassword,
  changePassword
} from '../services/authService';

import api from '../services/api';
import moduloService from '../services/moduloService';

/* ------------------------------------------------------------------ */
/*  Contexto                                                          */
/* ------------------------------------------------------------------ */

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);          // dados do utilizador
  const [loading, setLoading] = useState(true);    // bootstrapping
  const [moduleAccess, setModuleAccess] = useState({}); // cache {CORE:true}

  /* ----------------------------------------------------------------
     Bootstrap – verificar sessão ao montar
  ------------------------------------------------------------------ */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');

        // Sem token → não há sessão
        if (!token) throw new Error('Sem token');

        // Validar token com o backend — se expirado/inválido devolve 401
        let currentUser;
        try {
          const { data } = await api.get('/auth/verificar-sessao');
          currentUser = data?.usuario || data?.user || data;
        } catch {
          throw new Error('Sessão inválida ou expirada');
        }

        if (!currentUser) throw new Error('Sem sessão');

        /* ---------------- estudanteId ---------------- */
        let estudanteId = currentUser.estudanteId;
        if (!estudanteId && currentUser.tipo === 'estudante') {
          try {
            const { data } = await api.get(`/estudantes/usuario/${currentUser.id}`);
            estudanteId = data.id;
          } catch (err) {
            console.error('Erro ao obter estudanteId:', err);
          }
        }
        
        /* ---------------- perfil ativo ---------------- */
      let perfilAtivo = currentUser.perfilAtivo;
// ⚠️ SÓ busca perfil se NÃO for instituição, admin ou SUPER_ADMIN
if (!perfilAtivo && currentUser.tipo && 
    currentUser.tipo !== 'instituicao' && 
    currentUser.tipo !== 'admin' && 
    currentUser.tipo !== 'SUPER_ADMIN') {
  try {
    const { data } = await api.get('/perfis-usuario/atual');
    perfilAtivo = data;
  } catch (err) {
    console.warn('Perfil não disponível para tipo:', currentUser.tipo);
  }
}

        /* ---------------- instituicaoId - LÓGICA CORRIGIDA ---------------- */
        let instituicaoId = currentUser.instituicaoId;
        
        if (!instituicaoId) {
          // Para usuários do tipo 'instituicao', usar o instituicao_id do perfil ativo
          if (currentUser.tipo === 'instituicao' && perfilAtivo?.instituicao_id) {
            instituicaoId = perfilAtivo.instituicao_id;
            console.log('InstituicaoId obtido do perfil ativo:', instituicaoId);
          }
          // Para estudantes, buscar via vínculo ativo
          else if (currentUser.tipo === 'estudante' && estudanteId) {
            try {
              const { data } = await api.get(`/vinculos/estudante/${estudanteId}/ativo`);
              instituicaoId = data?.instituicao_id || null;
              console.log('InstituicaoId obtido do vínculo:', instituicaoId);
            } catch (err) {
              console.error('Erro ao obter instituição do vínculo:', err);
            }
          }
        }

        const userData = {
          ...currentUser,
          estudanteId,
          instituicaoId,   // ✅ Agora será populado corretamente
          perfilAtivo
        };
        
        console.log('Dados do usuário finalizados:', userData);
        setUser(userData);
        
        // Sempre salvar com a chave 'usuario' para consistência
        localStorage.setItem('usuario', JSON.stringify(userData));
        // Remover a chave antiga se existir
        if (localStorage.getItem('user')) {
          localStorage.removeItem('user');
        }
        
      } catch (err) {
        console.warn('Sem utilizador autenticado:', err.message);
        setUser(null);
        localStorage.removeItem('usuario');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  /* ----------------------------------------------------------------
     checkModuleAccess – consulta com cache + revalidação
  ------------------------------------------------------------------ */
  const checkModuleAccess = async (moduleCode) => {
    if (!user) return false;
    if (user.tipo === 'admin') return true;

    // true em cache → devolve logo
    if (moduleAccess[moduleCode] === true) return true;

    // false recente em cache → devolve logo para evitar spam
    const lastTs = moduleAccess.__ts ?? 0;
    if (
      moduleAccess[moduleCode] === false &&
      Date.now() - lastTs < 60_000 // 60 s
    ) {
      return false;
    }

    // chamada ao backend
    const access = await moduloService.verificarAcessoModulo(moduleCode, user);

    setModuleAccess((prev) => ({
      ...prev,
      [moduleCode]: access,
      __ts: Date.now()
    }));

    return access;
  };

  /* ----------------------------------------------------------------
     login
  ------------------------------------------------------------------ */
  /* ----------------------------------------------------------------
     login
  ------------------------------------------------------------------ */
  const loginUser = async (email, senha) => {
  // 🔑 MOVER O RESET PARA O INÍCIO, antes de qualquer operação
  localStorage.setItem('roleContext', 'auto');
  
  // 🔔 Disparar evento para o RoleContext detectar a mudança
  window.dispatchEvent(new CustomEvent('auth:roleReset'));
  
  const resp = await login(email, senha); // { mensagem, token, user }

  const u = resp.user;

  let estudanteId = null;
  if (u.tipo === 'estudante') {
    try {
      const { data } = await api.get(`/estudantes/usuario/${u.id}`);
      estudanteId = data.id;
    } catch (err) {
      console.error('Erro ao obter estudanteId:', err);
    }
  }

  let perfilAtivo = null;
  if (u.tipo && u.tipo !== 'instituicao' && u.tipo !== 'admin' && u.tipo !== 'SUPER_ADMIN') {
    try {
      const { data } = await api.get('/perfis-usuario/atual');
      perfilAtivo = data;
    } catch (err) {
      console.warn('Perfil não disponível para tipo:', u.tipo);
    }
  }

    /* instituicaoId - LÓGICA CORRIGIDA */
     let instituicaoId = u.instituicaoId;
  if (!instituicaoId) {
    if (u.tipo === 'instituicao' && perfilAtivo?.instituicao_id) {
      instituicaoId = perfilAtivo.instituicao_id;
    } else if (u.tipo === 'estudante' && estudanteId) {
      try {
        const { data } = await api.get(`/vinculos/estudante/${estudanteId}/ativo`);
        instituicaoId = data?.instituicao_id || null;
      } catch (err) {
        console.error('Erro ao obter instituição do vínculo:', err);
      }
    }
  }
    const userData = {
      ...u,
      estudanteId,
      instituicaoId,  // ✅ Agora será populado corretamente
      perfilAdmin: u.perfilAdmin,
      perfilAtivo
    };

    console.log('Dados do usuário após login:', userData);
    setUser(userData);
    localStorage.setItem('usuario', JSON.stringify(userData));
    setModuleAccess({}); // limpa cache

    // 🔔 Disparar evento final para garantir que RoleContext se atualize
    window.dispatchEvent(new CustomEvent('auth:changed'));

    return resp;
  };

  /* ----------------------------------------------------------------
     logout
  ------------------------------------------------------------------ */
  const logoutUser = async () => {
  try { await logout(); } catch {}
  setUser(null);
  setModuleAccess({});
  localStorage.removeItem('usuario');
  localStorage.removeItem('user');
  // 🧹 limpar papel escolhido para o próximo login começar em 'auto'
  localStorage.removeItem('roleContext');
};
  /* ----------------------------------------------------------------
     register
  ------------------------------------------------------------------ */
  const registerUser = async (userPayload) => {
    const resp = await register(userPayload);
    const u = resp.user;

    let estudanteId = null;
    if (u.tipo === 'estudante') {
      try {
        const { data } = await api.get(`/estudantes/usuario/${u.id}`);
        estudanteId = data.id;
      } catch (err) {
        console.error('Erro ao obter estudanteId:', err);
      }
    }

    /* perfil ativo */
    let perfilAtivo = null;
// ⚠️ SÓ busca perfil se NÃO for instituição, admin ou SUPER_ADMIN
if (u.tipo && 
    u.tipo !== 'instituicao' && 
    u.tipo !== 'admin' && 
    u.tipo !== 'SUPER_ADMIN') {
  try {
    const { data } = await api.get('/perfis-usuario/atual');
    perfilAtivo = data;
  } catch (err) {
    console.warn('Perfil não disponível para tipo:', u.tipo);
  }
}
    /* instituicaoId - LÓGICA CORRIGIDA */
    let instituicaoId = u.instituicaoId;
    
    if (!instituicaoId) {
      // Para usuários do tipo 'instituicao', usar o instituicao_id do perfil ativo
      if (u.tipo === 'instituicao' && perfilAtivo?.instituicao_id) {
        instituicaoId = perfilAtivo.instituicao_id;
      }
      // Para estudantes, buscar via vínculo ativo
      else if (u.tipo === 'estudante' && estudanteId) {
        try {
          const { data } = await api.get(`/vinculos/estudante/${estudanteId}/ativo`);
          instituicaoId = data?.instituicao_id || null;
        } catch (err) {
          console.error('Erro ao obter instituição do vínculo:', err);
        }
      }
    }

    const userData = {
    ...u,
    estudanteId,
    instituicaoId,
    perfilAdmin: u.perfilAdmin,
    perfilAtivo
  };

  setUser(userData);
  localStorage.setItem('usuario', JSON.stringify(userData));
  setModuleAccess({});

  return resp;
};



  /* ----------------------------------------------------------------
     Outras utilidades
  ------------------------------------------------------------------ */
  const verifyUserEmail = (token) => verifyEmail(token);
  const resendUserVerification = (email) => resendVerification(email);
  const requestUserPasswordReset = ({ identificador }) =>
    requestPasswordReset(identificador);
  const adicionarEmailParaRecuperacao = ({ identificador, email }) =>
    adicionarEmailRecuperacao(identificador, email);
  const resetUserPassword = (token, novaSenha) =>
    resetPassword(token, novaSenha);
  const changeUserPassword = (senhaAtual, novaSenha) =>
    changePassword(senhaAtual, novaSenha);

  /* ----------------------------------------------------------------
     provider value
  ------------------------------------------------------------------ */
  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login: loginUser,
    logout: logoutUser,
    register: registerUser,
    verifyEmail: verifyUserEmail,
    resendVerification: resendUserVerification,
    requestPasswordReset: requestUserPasswordReset,
    adicionarEmailRecuperacao: adicionarEmailParaRecuperacao,
    resetPassword: resetUserPassword,
    changePassword: changeUserPassword,
    checkModuleAccess
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default AuthProvider;